'use client';
import { RefObject, useEffect, useRef, MutableRefObject } from 'react';
import { gsap } from 'gsap';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';
import { ImageProps } from '@repo/ui/src/types/image';
import { classNames } from '@repo/ui/src/utils/classNames';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import Image from '@repo/ui/src/components/Image';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { useMounted } from '@hooks/useMounted';

interface GrillHeroProps {
  toutContent?: ToutContentProps;
  grillImage?: ImageProps;
}

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const gsapConfig = (
  ref: RefObject<HTMLElement>,
  tl: MutableRefObject<gsap.core.Timeline | undefined>,
) => {
  if (typeof window === 'undefined') return;

  const mm = gsap.matchMedia();

  if (tl.current) {
    tl.current.progress(0).kill();
  }

  mm.add(
    '(prefers-reduced-motion: no-preference)',
    (context) => {
      const ctx = context;

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: ref.current,
          scrub: 0.5,
          start: 'top center',
          end: 'bottom 40%',
        },
      });

      tl.current = timeline;

      timeline
        .addLabel('start')
        .from('.grill-hero-content', {
          translateY: 50,
          opacity: 0,
          duration: 1,
        })
        .delay(5)
        .addLabel('smoke')
        .fromTo(
          '.smoke-wrapper',
          {
            yPercent: 100,
            opacity: 0,
          },
          {
            yPercent: 30,
            opacity: 0.8,
            duration: 3,
          },
          'smoke',
        )
        .from(
          '.smoke-image-1',
          {
            xPercent: -12,
            duration: 3,
          },
          'smoke',
        )
        .from(
          '.smoke-image-2',
          {
            xPercent: 6,
            duration: 3,
          },
          'smoke',
        )
        .to(
          '.grill-hero-content',
          {
            opacity: 0,
            duration: 1,
            delay: 1,
          },
          'smoke',
        );

      return () => {
        ctx.revert();
      };
    },
    ref,
  );
};

const GrillHero = ({ grillImage, toutContent }: GrillHeroProps) => {
  const ref = useRef<HTMLElement>(null);
  const tl = useRef<gsap.core.Timeline>();
  const isMounted = useMounted();
  const smokeImgUrl = buildImageSrc({ endpoint: 'traeger', name: 'transparent-smoke' });

  useEffect(() => {
    if (!isMounted) return;

    gsapConfig(ref, tl);

    return () => {
      if (tl.current) {
        tl.current.kill();
      }
      if (typeof window !== 'undefined') {
        ScrollTrigger.getAll().forEach((st) => st.kill());
      }
    };
  }, [isMounted]);

  if (!grillImage) return null;
  return (
    <section ref={ref}>
      <div className="grill-hero text-center tw-bg-surface-800 tw-bg-speckle">
        <ToutContent
          dark
          divider
          flexible
          headingStyleVariant="h1"
          {...toutContent}
          className="grill-hero-content tw-sticky tw-top-0 tw-left-0 tw-right-0 tw-px-6 tw-py-16 lg:tw-px-16"
        />

        <div className="tw-overflow-hidden tw-relative tw-pointer-events-none">
          <div className="tw-relative tw-mx-auto tw-w-full md:tw-w-half tw-z-10">
            <div className="hero-grill-image tw--mb-[20%] tw-aspect-square tw-relative">
              <Image
                layout="fill"
                src={buildImageSrc(grillImage?.image)}
                alt={grillImage?.image?.name}
              />
            </div>
          </div>

          <div className="smoke-wrapper tw-absolute tw-bottom-0 tw-left-0 tw-w-full tw-pt-[32.25%] tw-z-0 motion-reduce:tw-opacity-80 motion-reduce:tw-translate-y-1/3">
            {[1, 2].map((i: number) => (
              <div
                key={i}
                className={`smoke-image-${i} tw-w-full tw-h-full tw-absolute tw-bottom-0`}
              >
                <Image
                  src={smokeImgUrl}
                  className={classNames(i === 2 && '-tw-scale-x-200')}
                  role="presentation"
                  alt=""
                  layout="fill"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GrillHero;
